import { DestroyRef, inject, Injectable, Injector, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { LastRefreshResponse } from '@anduin/api';
import { getLoadedResults, Logger } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { GridApi } from 'ag-grid-community';
import { combineLatest, defer, EMPTY, filter, repeat, Subject, Subscription, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { EmailDeliveryHubState, LoadLastRefresh, LoadMetrics, LoadSettings } from '../state';


const POLLING_INTERVAL_MS = 10000; // 10 seconds
const MAX_POLLING_ATTEMPTS_NO_PROGRESS = 3; // backend never started refresh
const MAX_POLLING_ATTEMPTS_IN_PROGRESS = 12; // refresh is running, wait for completion

const FALLBACK_MAX_TIME_BETWEEN_REFRESHES = 600000; // 10 minutes

@Injectable()
export class EmailDeliveryHubSentRefreshService {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly injector = inject(Injector);

  private pollingSubscription: Subscription | null = null;
  private scheduledRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private stopPolling$ = new Subject<void>();
  private initialized = false;
  private isSentTabActive: Signal<boolean>;
  private readyToRefresh = false;
  private initialLoadDone = false;
  private _gridApi: GridApi | undefined;

  setGridApi(gridApi: GridApi): void {
    this._gridApi = gridApi;
  }

  init(isSentTabActive: Signal<boolean>): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.isSentTabActive = isSentTabActive;
    this.store.dispatch(new LoadSettings());

    toObservable(isSentTabActive, { injector: this.injector })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
      )
      .subscribe(() => {
        if (!this.initialLoadDone) {
          this.initialLoadDone = true;
          this.store.dispatch(new LoadLastRefresh());
          this.waitForInitialLoad();
        } else if (this.readyToRefresh) {
          this.readyToRefresh = false;
          this.startPolling();
        }
      });
  }

  destroy(): void {
    this.cleanup();
    this.initialized = false;
    this.readyToRefresh = false;
    this.initialLoadDone = false;
  }

  private waitForInitialLoad(): void {
    combineLatest([
      getLoadedResults(this.store.select(EmailDeliveryHubState.getSettingsLoadable), true),
      getLoadedResults(this.store.select(EmailDeliveryHubState.getLastRefresh), true),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        tap(([settings, lastRefresh]) => this.handleInitialLoad(settings.maxTimeBetweenRefreshes, lastRefresh)),
      )
      .subscribe();
  }

  private handleInitialLoad(maxTimeBetweenRefreshes: number, lastRefresh: LastRefreshResponse): void {
    if (lastRefresh.inProgress) {
      this.startPolling();
    } else {
      this.scheduleNextRefresh(maxTimeBetweenRefreshes, lastRefresh.triggeredAt);
    }
  }

  private scheduleNextRefresh(maxTimeBetweenRefreshes: number, triggeredAt: string): void {
    const triggeredAtTime = new Date(triggeredAt).getTime();
    const nextRefreshTime = triggeredAtTime + maxTimeBetweenRefreshes;
    const now = Date.now();
    const delay = Math.max(0, nextRefreshTime - now);

    this.logger.info(`EmailDeliveryHubRefreshService :: Scheduling next refresh check in ${delay}ms`);

    this.scheduledRefreshTimeout = setTimeout(() => {
      if (this.isSentTabActive()) {
        this.startPolling();
      } else {
        this.logger.info('EmailDeliveryHubRefreshService :: Refresh due but not on sent tab, marking ready to refresh');
        this.readyToRefresh = true;
      }
    }, delay);
  }

  private startPolling(): void {
    if (this.pollingSubscription) {
      return;
    }

    this.logger.info('EmailDeliveryHubRefreshService :: Starting polling for refresh completion');
    this.stopPolling$ = new Subject<void>();
    let attempts = 0;
    let sawInProgress = false;
    const maxTimeBetweenRefreshes = this.store.selectSnapshot(EmailDeliveryHubState.getSettingsLoadable)?.data?.maxTimeBetweenRefreshes
      || FALLBACK_MAX_TIME_BETWEEN_REFRESHES;

    this.pollingSubscription = defer(() => {
      if (!this.isSentTabActive()) {
        this.logger.info('EmailDeliveryHubRefreshService :: Sent tab no longer active, deferring refresh');
        this.readyToRefresh = true;
        this.stopPolling();
        return EMPTY;
      }

      attempts++;
      this.store.dispatch(new LoadLastRefresh(true));
      return getLoadedResults(this.store.select(EmailDeliveryHubState.getLastRefresh), true).pipe(take(1));
    }).pipe(
      switchMap(lastRefresh => {
        if (lastRefresh?.inProgress) {
          sawInProgress = true;
        }

        const isComplete = !lastRefresh?.inProgress
          && new Date(lastRefresh?.triggeredAt).getTime() + maxTimeBetweenRefreshes > Date.now();

        if (isComplete) {
          this.onRefreshComplete(lastRefresh);
          return EMPTY;
        }

        const maxAttempts = sawInProgress ? MAX_POLLING_ATTEMPTS_IN_PROGRESS : MAX_POLLING_ATTEMPTS_NO_PROGRESS;

        if (attempts >= maxAttempts) {
          this.logger.warn(`EmailDeliveryHubRefreshService :: Max polling attempts (${maxAttempts}) reached, stopping`);
          this.stopPollingAndScheduleRetry();
          return EMPTY;
        }

        return timer(POLLING_INTERVAL_MS);
      }),
      repeat(),
      takeUntil(this.stopPolling$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /**
   * If polling reaches the maximum number of attempts without seeing progress or completion,
   * stop polling and schedule a retry after the max time between refreshes.
   * This handles cases where the backend refresh process may have failed to start or complete, ensuring we don't poll indefinitely.
   * @private
   */
  private stopPollingAndScheduleRetry(): void {
    this.stopPolling();

    const settings = this.store.selectSnapshot(EmailDeliveryHubState.getSettingsLoadable);
    if (settings?.data?.maxTimeBetweenRefreshes) {
      const delay = settings.data.maxTimeBetweenRefreshes;
      this.logger.info(`EmailDeliveryHubRefreshService :: Scheduling retry in ${delay}ms`);
      this.scheduledRefreshTimeout = setTimeout(() => {
        if (this.isSentTabActive()) {
          this.startPolling();
        } else {
          this.readyToRefresh = true;
        }
      }, delay);
    }
  }

  private onRefreshComplete(lastRefresh: LastRefreshResponse): void {
    this.logger.info('EmailDeliveryHubRefreshService :: Refresh completed, updating grid');
    this.stopPolling();

    if (lastRefresh.success) {
      this._gridApi?.refreshServerSide({ purge: false });
      this.store.dispatch(new LoadMetrics());
    }

    // this.store.dispatch(new LoadLastRefresh(true));

    const settings = this.store.selectSnapshot(EmailDeliveryHubState.getSettingsLoadable);
    if (settings?.data?.maxTimeBetweenRefreshes && lastRefresh?.triggeredAt) {
      this.scheduleNextRefresh(settings.data.maxTimeBetweenRefreshes, lastRefresh.triggeredAt);
    }
  }

  private stopPolling(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  private cleanup(): void {
    this.stopPolling();

    if (this.scheduledRefreshTimeout) {
      clearTimeout(this.scheduledRefreshTimeout);
      this.scheduledRefreshTimeout = null;
    }
  }
}
